import React, { lazy } from 'react'
import { Link, Navigate } from 'react-router-dom'
import type { RouteObject } from 'react-router-dom'
import { Helmet } from 'react-helmet'

import Layout from '../views/layout'
import { store } from '../reducers'

const Login = lazy(() => import('../views/login'))
const Home = lazy(() => import('../views/register'))
const RegisterAgreement = lazy(() => import('../views/register/agreement'))
const AccountOverview = lazy(() => import('../views/account'))
const UserOverview = lazy(() => import('../views/user'))
const MyContract = lazy(() => import('../views/myContract'))
const ContractDetail = lazy(() => import('../views/myContract/conDetail'))
const ContractResult = lazy(() => import('../views/myContract/conResult'))
const UserInfo = lazy(() => import('../views/user/useInfo'))
const NameAuth = lazy(() => import('../views/auth'))
const BankCard = lazy(() => import('../views/card'))
const BankDetail = lazy(() => import('../views/cardDetail'))
const AddBankCard = lazy(() => import('../views/addCard'))
const Withdraw = lazy(() => import('../views/withdraw'))
const PayIncome = lazy(() => import('../views/payIncome'))
const PayIncomeDetail = lazy(() => import('../views/payIncome/detail'))
const ChangePassword = lazy(() => import('../views/changePassword'))
const CancelAccount = lazy(() => import('../views/cancelAccount'))
const userAgreement = lazy(() => import('../views/userAgreement'))
const SignAgreement = lazy(() => import('../views/signAgreement'))

export const asyncRoutes: RouteObject[] = [
  {
    path: '/account',
    element: <Layout />,
    children: [
      {
        index: true,
        element: suspense(AccountOverview, { title: '账户总览' }),
      },
      {
        path: 'payincome',
        children: [
          {
            index: true,
            element: suspense(PayIncome, { title: '收支明细' }),
          },
          {
            path: 'detail/:type/:code',
            element: suspense(PayIncomeDetail, { title: '收支明细' }),
          },
        ],
      },
      {
        path: 'withdraw',
        element: suspense(Withdraw, { title: '提现' }),
      },
    ],
  },
  {
    path: '/user',
    element: <Layout />,
    children: [
      {
        index: true,
        element: suspense(UserOverview, { title: '我的' }),
      },
      {
        path: 'userInfo',
        element: suspense(UserInfo, { title: '个人信息' }),
      },
      {
        path: 'myContract',
        children: [
          {
            index: true,
            element: suspense(MyContract, { title: '我的合同' }),
          },
          {
            path: 'detail',
            element: suspense(ContractDetail, { title: '合同详情' }),
          },
          {
            path: 'result',
            element: suspense(ContractResult, { title: '合同签约返回' }),
          },
        ],
      },
      {
        path: 'nameAuth',
        element: suspense(NameAuth, { title: '实名认证' }),
      },
      {
        path: 'bank',
        children: [
          {
            index: true,
            element: suspense(BankCard, { title: '银行卡包' }),
          },
          {
            path: ':cardId',
            element: suspense(BankDetail, { title: '银行卡包' }),
          },
          {
            path: 'add',
            element: suspense(AddBankCard, { title: '银行卡包' }),
          },
        ],
      },
      {
        path: 'changePassword',
        element: suspense(ChangePassword, { title: '修改密码' }),
      },
      {
        path: 'cancelAccount',
        element: suspense(CancelAccount, { title: '注销账号' }),
      },
      {
        path: 'agreement',
        element: suspense(userAgreement, { title: '平台用户协议' }),
      },
    ],
  },
]

export const constantRoutes: RouteObject[] = [
  {
    path: '/',
    element: <Navigate replace to="/login" />,
  },
  {
    path: '/login',
    element: suspense(Login, { title: '登录' }),
  },
  {
    path: '/register/*',
    children: [
      {
        index: true,
        element: suspense(Home, { title: '注册' }),
      },
      {
        path: 'agreement',
        element: suspense(RegisterAgreement, { title: '平台用户协议' }),
      },
    ],
  },
  {
    path: '/sign',
    element: suspense(SignAgreement, { title: '平台用户协议' }),
  },
  {
    path: '*',
    element: <NoMatch />,
  },
]

interface MetaData {
  title: string
}

function suspense(Component: any, metaData: MetaData) {
  const { title } = metaData

  return (
    <React.Suspense fallback={<>...</>}>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Component />
    </React.Suspense>
  )
}

function NoMatch() {
  const state = store.getState()

  if (state.user.token) {
    return (
      <div>
        <h2>Nothing to see here!</h2>
        <p>
          <Link to="/login">Go to the login page</Link>
        </p>
      </div>
    )
  }
  return <Navigate replace to="/login" />
}
